import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api } from '../../utils/api';
import { Search, Download, Eye, Loader2, MapPin, Phone, Mail, Calendar } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const STATUS_OPTIONS = ['NEW', 'CONTACTED', 'SITE_VISIT_SCHEDULED', 'SITE_VISIT_DONE', 'QUOTE_SENT', 'CONVERTED', 'REJECTED', 'ON_HOLD'];
const STATUS_COLORS = {
  NEW: 'bg-yellow-100 text-yellow-800',
  CONTACTED: 'bg-blue-100 text-blue-800',
  SITE_VISIT_SCHEDULED: 'bg-purple-100 text-purple-800',
  SITE_VISIT_DONE: 'bg-indigo-100 text-indigo-800',
  QUOTE_SENT: 'bg-cyan-100 text-cyan-800',
  CONVERTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  ON_HOLD: 'bg-gray-100 text-gray-800'
};

const CATEGORY_LABELS = {
  broadband_only: 'Broadband Only',
  broadband_iptv: 'Broadband + IPTV',
  broadband_ott: 'Broadband + OTT',
  broadband_iptv_ott: 'Complete Package'
};

export const AdminLeads = () => {
  const { toast } = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status_filter: '',
    category: ''
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50,
    total: 0
  });

  useEffect(() => {
    fetchLeads();
    // Auto-refresh leads every 30 seconds
    const interval = setInterval(fetchLeads, 30000);
    return () => clearInterval(interval);
  }, [filters, pagination.skip]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = {
        skip: pagination.skip,
        limit: pagination.limit,
        ...(filters.search && { search: filters.search }),
        ...(filters.status_filter && { status_filter: filters.status_filter }),
        ...(filters.category && { category: filters.category }),
        _t: Date.now() // Cache buster
      };
      const data = await api.adminGetLeads(params);
      setLeads(data.leads);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load leads',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (leadId) => {
    try {
      const lead = await api.adminGetLead(leadId);
      setSelectedLead(lead);
      setDetailsOpen(true);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load lead details',
        variant: 'destructive'
      });
    }
  };

  const handleUpdateStatus = async (leadId, newStatus, note = '') => {
    try {
      await api.adminUpdateLeadStatus(leadId, { status: newStatus, note });
      toast({
        title: 'Success',
        description: 'Lead status updated'
      });
      fetchLeads();
      if (selectedLead?.id === leadId) {
        const updated = await api.adminGetLead(leadId);
        setSelectedLead(updated);
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        ...(filters.status_filter && { status_filter: filters.status_filter }),
        ...(filters.category && { category: filters.category })
      };
      const response = await api.adminExportLeads(params);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast({
        title: 'Success',
        description: 'Leads exported successfully'
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to export leads',
        variant: 'destructive'
      });
    }
  };

  return (
    // eslint-disable-next-line
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leads Management</h1>
          <p className="text-gray-600 mt-1">View and manage broadband lead requests (Auto-refreshes every 30s)</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={fetchLeads}
            variant="outline"
            className="border-red-600 text-red-600 hover:bg-red-50"
          >
            <Loader2 size={20} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={handleExport}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download size={20} className="mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, phone, ref ID..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
            <select
              value={filters.status_filter}
              onChange={(e) => setFilters({ ...filters, status_filter: e.target.value })}
              className="h-10 px-3 rounded-md border border-gray-300"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="h-10 px-3 rounded-md border border-gray-300"
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <Button
              onClick={fetchLeads}
              className="bg-red-600 hover:bg-red-700"
            >
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No leads found. Adjust filters or wait for new submissions.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Ref ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Mobile</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Price</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">City</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Created</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm font-mono text-gray-900">
                          {lead.reference_id}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">{lead.full_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-900">{lead.mobile}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          <div>{lead.plan_snapshot?.plan_name}</div>
                          <div className="text-xs text-gray-500">
                            {lead.plan_snapshot?.speed_mbps} Mbps
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900">
                          Rs. {lead.calculated_price?.toLocaleString()}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {lead.address?.city}
                        </td>
                        <td className="py-3 px-4">
                          <select
                            value={lead.status}
                            onChange={(e) => handleUpdateStatus(lead.id, e.target.value)}
                            className={`text-xs px-2 py-1 rounded font-semibold ${STATUS_COLORS[lead.status] || 'bg-gray-100 text-gray-800'}`}
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {status.replace(/_/g, ' ')}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetails(lead.id)}
                          >
                            <Eye size={16} className="mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} leads
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.skip === 0}
                    onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.skip + pagination.limit >= pagination.total}
                    onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Lead Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lead Details - {selectedLead?.reference_id}</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-6 mt-4">
              {/* Customer Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Full Name</p>
                      <p className="font-semibold">{selectedLead.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Mobile</p>
                      <p className="font-semibold flex items-center">
                        <Phone size={16} className="mr-2" />
                        {selectedLead.mobile}
                      </p>
                    </div>
                    {selectedLead.whatsapp && (
                      <div>
                        <p className="text-sm text-gray-600">WhatsApp</p>
                        <p className="font-semibold">{selectedLead.whatsapp}</p>
                      </div>
                    )}
                    {selectedLead.email && (
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-semibold flex items-center">
                          <Mail size={16} className="mr-2" />
                          {selectedLead.email}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Plan Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Plan Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Plan Name</p>
                      <p className="font-semibold">{selectedLead.plan_snapshot?.plan_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Speed</p>
                      <p className="font-semibold">{selectedLead.plan_snapshot?.speed_mbps} Mbps</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Category</p>
                      <p className="font-semibold">{CATEGORY_LABELS[selectedLead.plan_snapshot?.category]}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Price</p>
                      <p className="font-semibold text-red-600 text-lg">
                        Rs. {selectedLead.calculated_price?.toLocaleString()}/year
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <MapPin size={20} className="mr-2" />
                    Installation Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {selectedLead.address?.latitude && (
                    <p className="text-sm text-gray-600">
                      <strong>GPS:</strong> {selectedLead.address.latitude.toFixed(6)}, {selectedLead.address.longitude.toFixed(6)}
                      {selectedLead.address.accuracy && ` (+/-${selectedLead.address.accuracy.toFixed(0)}m)`}
                    </p>
                  )}
                  <p className="font-semibold">
                    {selectedLead.address?.street && `${selectedLead.address.street}, `}
                    {selectedLead.address?.area && `${selectedLead.address.area}, `}
                    {selectedLead.address?.city}, {selectedLead.address?.state} {selectedLead.address?.pincode}
                  </p>
                  {selectedLead.address?.landmark && (
                    <p className="text-sm text-gray-600">
                      <strong>Landmark:</strong> {selectedLead.address.landmark}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Preferences */}
              {(selectedLead.preferred_date || selectedLead.time_slot || selectedLead.notes) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Installation Preferences</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {selectedLead.preferred_date && (
                      <p>
                        <strong>Preferred Date:</strong> {selectedLead.preferred_date}
                      </p>
                    )}
                    {selectedLead.time_slot && (
                      <p>
                        <strong>Time Slot:</strong> {selectedLead.time_slot}
                      </p>
                    )}
                    {selectedLead.notes && (
                      <p>
                        <strong>Notes:</strong> {selectedLead.notes}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Internal Notes */}
              {selectedLead.internal_notes && selectedLead.internal_notes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Internal Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedLead.internal_notes.map((note, idx) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded">
                          <p className="text-sm">{note.note}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            By {note.added_by} on {new Date(note.added_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
